import { acceptHMRUpdate, defineStore } from "pinia";
import type {
  RequirementTemplate,
  StaffReq,
  RequirementStats,
  StaffRequirReviewPayload,
  StatsBySection,
} from "~/types/requirement";
import { normalizeStaffReqs, normalizeTemplates } from "~/utils/requirement-normalizer";

interface RequirementsState {
  templates: RequirementTemplate[];
  staffReqs: StaffReq[];
  pendingReviews: StaffReq[];
  stats: RequirementStats | null;
}

export const useRequirementsStore = defineStore("requirements", () => {
  const auth = useAuthStore();
  const toast = useToast();

  const state = ref<RequirementsState>({
    templates: [],
    staffReqs: [],
    pendingReviews: [],
    stats: null,
  });

  const createTemplate = async (templateData: Partial<RequirementTemplate>) => {
    try {
      const resp = await auth.api<{ template: RequirementTemplate; message: string }>(
        "post",
        "/requirements",
        templateData,
      );
      toast.add({
        icon: "i-heroicons-document-check",
        title: "Success",
        description: resp.message || "Requirement template created successfully",
        color: "success",
        duration: 2500,
      });
      return resp;
    } catch (error: any) {
      console.error("Error creating requirement template:", error);
      const description =
        error.data?.message || error.message || "Could not create requirement template.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const getTemplates = async (query?: Record<string, any>) => {
    try {
      const resp = await auth.api<{ data: RequirementTemplate[]; meta: any }>(
        "get",
        "/requirements",
        { queryString: query },
      );
      state.value.templates = normalizeTemplates(resp.data);
      return { data: state.value.templates, meta: resp.meta };
    } catch (error: any) {
      console.error("Error fetching requirement templates:", error);
      const description =
        error.data?.message || error.message || "Could not fetch requirement templates.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const getTemplate = async (templateId: string): Promise<RequirementTemplate> => {
    try {
      const resp = await auth.api<RequirementTemplate>("get", `/requirements/${templateId}`);
      return resp;
    } catch (error: any) {
      console.error("Error fetching requirement template:", error);
      const description =
        error.data?.message || error.message || "Could not fetch requirement template.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const updateTemplate = async (templateId: string, updateData: Partial<RequirementTemplate>) => {
    try {
      const resp = await auth.api<{ template: RequirementTemplate; message: string }>(
        "put",
        `/requirements/${templateId}`,
        updateData,
      );
      toast.add({
        icon: "i-heroicons-document-check",
        title: "Success",
        description: resp.message || "Requirement template updated successfully",
        color: "success",
        duration: 2500,
      });
      return resp;
    } catch (error: any) {
      console.error("Error updating requirement template:", error);
      const description =
        error.data?.message || error.message || "Could not update requirement template.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const deleteTemplate = async (templateId: string) => {
    try {
      const resp = await auth.api<{ message: string }>("delete", `/requirements/${templateId}`);
      // Remove from local state reactively (in-place mutation so shared refs stay synced)
      const index = state.value.templates.findIndex((t) => t.id === templateId);
      if (index !== -1) {
        state.value.templates.splice(index, 1);
      }
      toast.add({
        icon: "i-heroicons-document-check",
        title: "Success",
        description: resp.message || "Requirement template deleted successfully",
        color: "success",
        duration: 2500,
      });
      return true;
    } catch (error: any) {
      console.error("Error deleting requirement template:", error);
      const description =
        error.data?.message || error.message || "Could not delete requirement template.";
      toast.add({ title: "Error", description, color: "error" });
      return false;
    }
  };

  const getStaffRequirements = async (query?: Record<string, any>) => {
    try {
      // If template_id is provided, use the template-specific endpoint
      if (query?.template_id) {
        const resp = await auth.api("get", `/requirements/${query.template_id}/staff`);
        state.value.staffReqs = resp as StaffReq[];
        return { data: state.value.staffReqs, meta: { total: resp.length } };
      }

      // Otherwise use the general endpoint with pagination
      const resp = await auth.api<{ data: StaffReq[]; meta: any }>("get", "/staff", {
        queryString: query,
      });
      state.value.staffReqs = resp.data;
      return { data: state.value.staffReqs, meta: resp.meta };
    } catch (error: any) {
      console.error("Error fetching staff requirements:", error);
      const description =
        error.data?.message || error.message || "Could not fetch staff requirements.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const getMyRequirements = async (): Promise<{ data: StaffReq[]; meta: any }> => {
    try {
      const resp = await auth.api<{ data: StaffReq[]; meta: any }>("get", "/staff/me");
      state.value.staffReqs = resp.data;
      return { data: resp.data, meta: resp.meta };
    } catch (error: any) {
      console.error("Error fetching my requirements:", error);
      const description =
        error.data?.message || error.message || "Could not fetch your requirements.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const getPendingReviews = async () => {
    try {
      const resp = await auth.api<StaffReq[]>("get", "/staff/pending-reviews");
      state.value.pendingReviews = resp;
      return state.value.pendingReviews;
    } catch (error: any) {
      console.error("Error fetching pending reviews:", error);
      const description =
        error.data?.message || error.message || "Could not fetch pending reviews.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(error);
    }
  };

  const submitRevision = async (payload: FormData) => {
    try {
      const resp = await auth.api<{ revision: any; message: string }>(
        "post",
        "/staff/submit",
        payload,
      );
      toast.add({
        icon: "i-heroicons-document-arrow-up",
        title: "Success",
        description: resp.message || "Revision submitted successfully",
        color: "success",
        duration: 2500,
      });
      return resp;
    } catch (error: any) {
      console.error("Error submitting revision:", error);
      const description = error.data?.message || error.message || "Could not submit revision.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const reviewRequirement = async (payload: StaffRequirReviewPayload) => {
    try {
      const resp = await auth.api<{ revision: any; message: string }>(
        "post",
        "/staff/review",
        payload,
      );
      toast.add({
        icon: "i-heroicons-document-check",
        title: "Success",
        description: resp.message || "Requirement reviewed successfully",
        color: "success",
        duration: 2500,
      });
      return resp;
    } catch (error: any) {
      console.error("Error reviewing requirement:", error);
      const description = error.data?.message || error.message || "Could not review requirement.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const getStats = async (): Promise<RequirementStats> => {
    try {
      const [templates, staffStats] = await Promise.all([
        auth.api<{ count: number }>("get", "/requirements/count"),
        auth.api<{
          pending: number;
          submitted: number;
          verified: number;
          rejected: number;
          expiringSoon: number;
        }>("get", "/staff/stats"),
      ]);

      state.value.stats = {
        pendingReviews: staffStats.submitted,
        verified: staffStats.verified,
        expiringSoon: staffStats.expiringSoon,
        totalTemplates: templates.count,
      };

      return state.value.stats;
    } catch (error: any) {
      console.error("Error fetching stats:", error);
      throw new Error("Could not fetch requirement statistics");
    }
  };

  const getStatsBySection = async (): Promise<StatsBySection> => {
    try {
      return await auth.api<StatsBySection>("get", "/staff/stats-by-section");
    } catch (error: any) {
      console.error("Error fetching stats by section:", error);
      throw new Error("Could not fetch stats by section");
    }
  };

  // ==================== Local Mutation Methods (for SSE handlers) ====================

  const updateMyRequirementLocal = (updatedReq: any) => {
    const normalized = normalizeStaffReqs([updatedReq])[0];
    if (!normalized) return;
    const index = state.value.staffReqs.findIndex((r) => r.id === normalized.id);
    if (index !== -1) {
      state.value.staffReqs[index] = { ...state.value.staffReqs[index], ...normalized };
    } else {
      state.value.staffReqs.unshift(normalized);
    }
  };

  const addStaffReqLocal = (staffReq: any) => {
    const normalized = normalizeStaffReqs([staffReq])[0];
    if (!normalized) return;
    const exists = state.value.staffReqs.some((r) => r.id === normalized.id);
    if (!exists) {
      state.value.staffReqs.unshift(normalized);
    }
    if (normalized.status === "submitted") {
      const inPending = state.value.pendingReviews.some((r) => r.id === normalized.id);
      if (!inPending) {
        state.value.pendingReviews.unshift(normalized);
      }
    }
  };

  const updateStaffReqLocal = (staffReq: any) => {
    const normalized = normalizeStaffReqs([staffReq])[0];
    if (!normalized) return;
    const index = state.value.staffReqs.findIndex((r) => r.id === normalized.id);
    if (index !== -1) {
      state.value.staffReqs[index] = { ...state.value.staffReqs[index], ...normalized };
    }
    const pendingIndex = state.value.pendingReviews.findIndex((r) => r.id === normalized.id);
    if (pendingIndex !== -1) {
      state.value.pendingReviews[pendingIndex] = {
        ...state.value.pendingReviews[pendingIndex],
        ...normalized,
      };
    }
  };

  const removeStaffReqLocal = (id: string) => {
    state.value.staffReqs = state.value.staffReqs.filter((r) => r.id !== id);
    state.value.pendingReviews = state.value.pendingReviews.filter((r) => r.id !== id);
  };

  const refreshStats = async () => {
    try {
      const [templates, staffStats] = await Promise.all([
        auth.api<{ count: number }>("get", "/requirements/count"),
        auth.api<{
          pending: number;
          submitted: number;
          verified: number;
          rejected: number;
          expiringSoon: number;
        }>("get", "/staff/stats"),
      ]);
      state.value.stats = {
        pendingReviews: staffStats.submitted,
        verified: staffStats.verified,
        expiringSoon: staffStats.expiringSoon,
        totalTemplates: templates.count,
      };
    } catch (error) {
      console.error("Error refreshing requirement stats:", error);
    }
  };

  return {
    ...toRefs(state.value),
    createTemplate,
    getTemplates,
    getTemplate,
    updateTemplate,
    deleteTemplate,
    getStaffRequirements,
    getMyRequirements,
    getPendingReviews,
    submitRevision,
    reviewRequirement,
    getStats,
    getStatsBySection,
    updateMyRequirementLocal,
    addStaffReqLocal,
    updateStaffReqLocal,
    removeStaffReqLocal,
    refreshStats,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useRequirementsStore, import.meta.hot));
}
